import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../../theme';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Table } from '../../../components/ui/Table';
import toast from 'react-hot-toast';
import { GuardData } from '../../manage_guard/service/guardService';
import {
    getAttendanceHistory,
    getMonthName,
    getCurrentDateInfo,
    getMonthlySalaryBreakdown,
    SalaryBreakdownPeriod,
    AttendanceHistory,
} from '../service/attendanceService';
import GuardSalarySummaryModal from './GuardSalarySummaryModal';
import { FiEye, FiTrendingUp, FiTrash2, FiClock } from 'react-icons/fi';
import { deleteGuardDeploymentHistory } from '../../deployment/service/deploymentService';
import { Select } from '../../../components/ui/Select';

const LoadingContainer = styled.div`
  text-align: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid ${theme.colors.light.border.light};
  border-top: 4px solid ${theme.colors.primary[500]};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto ${theme.spacing[4]};

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  color: ${theme.colors.light.text.secondary};
`;

const EmptyStateIcon = styled.div`
  font-size: 48px;
  margin-bottom: ${theme.spacing[4]};
  opacity: 0.5;
`;

const EmptyStateText = styled.p`
  font-size: ${theme.typography.fontSize.lg};
  margin: 0;
`;

const Badge = styled.span<{ variant?: 'success' | 'danger' | 'warning' | 'info' }>`
  display: inline-block;
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
  background-color: ${({ variant = 'success' }) => {
        if (variant === 'success') return `${theme.colors.accent.success}20`;
        if (variant === 'danger') return `${theme.colors.accent.danger}20`;
        if (variant === 'info') return `${theme.colors.accent.info}20`;
        return `${theme.colors.accent.warning}20`;
    }};
  color: ${({ variant = 'success' }) => {
        if (variant === 'success') return theme.colors.accent.success;
        if (variant === 'danger') return theme.colors.accent.danger;
        if (variant === 'info') return theme.colors.accent.info;
        return theme.colors.accent.warning;
    }};
`;

const PaymentStatusBadge = styled.span<{ status: 'paid' | 'unpaid' | 'current' }>`
  display: inline-block;
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
  background-color: ${({ status }) => {
        if (status === 'paid') return `${theme.colors.accent.success}20`;
        if (status === 'unpaid') return `${theme.colors.accent.danger}20`;
        return `${theme.colors.primary[500]}20`;
    }};
  color: ${({ status }) => {
        if (status === 'paid') return theme.colors.accent.success;
        if (status === 'unpaid') return theme.colors.accent.danger;
        return theme.colors.primary[600];
    }};
`;

const GuardInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[4]};
  background: ${theme.colors.light.background.secondary};
  border-radius: ${theme.borderRadius.lg};
  margin-bottom: ${theme.spacing[4]};
`;

const GuardImage = styled.img`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  object-fit: cover;
`;

const GuardDetails = styled.div`
  flex: 1;
`;

const GuardName = styled.div`
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.primary};
  margin-bottom: ${theme.spacing[1]};
`;

const GuardMeta = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.secondary};
`;

interface AttendanceHistoryModalProps {
    guard: GuardData;
    isOpen: boolean;
    onClose: () => void;
    companyId: string;
}

const SalarySummaryButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  margin-left: auto;
`;

const TabContainer = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[4]};
  border-bottom: 1px solid ${theme.colors.light.border.light};
  padding-bottom: ${theme.spacing[2]};
`;

const Tab = styled.button<{ active: boolean }>`
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${({ active }) => active ? theme.colors.primary[600] : theme.colors.light.text.secondary};
  background: ${({ active }) => active ? `${theme.colors.primary[500]}10` : 'transparent'};
  border: none;
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: ${theme.colors.primary[600]};
    background: ${theme.colors.light.background.tertiary};
  }
`;

const HistoryCard = styled.div`
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.light.border.light};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[4]};
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);

  &:last-child {
    margin-bottom: 0;
  }
`;

const CardTitle = styled.h4`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.primary[600]};
  margin-bottom: ${theme.spacing[3]};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: ${theme.spacing[2]} 0;
  border-bottom: 1px dashed ${theme.colors.light.border.light};

  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.span`
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.light.text.secondary};
`;

const DetailValue = styled.span`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.light.text.primary};
  text-align: right;
`;

const AttendanceHistoryModal: React.FC<AttendanceHistoryModalProps> = ({
    guard,
    isOpen,
    onClose,
    companyId,
}) => {
    const [history, setHistory] = useState<AttendanceHistory[]>([]);
    const [loading, setLoading] = useState(false);
    const [isGuardSalarySummaryOpen, setIsGuardSalarySummaryOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<'attendance' | 'deployment' | 'breakdown'>('breakdown');

    const currentDateInfo = getCurrentDateInfo();
    const [calcMonth, setCalcMonth] = useState(currentDateInfo.month);
    const [calcYear, setCalcYear] = useState(currentDateInfo.year);
    const [breakdown, setBreakdown] = useState<SalaryBreakdownPeriod[]>([]);

    const parseDate = (dateVal: any): string => {
        if (!dateVal) return '—';

        let date: Date | null = null;
        if (dateVal instanceof Date) {
            date = dateVal;
        } else if (typeof dateVal.toDate === 'function') {
            date = dateVal.toDate();
        } else if (dateVal.seconds) {
            date = new Date(dateVal.seconds * 1000);
        } else if (typeof dateVal === 'string') {
            const dmyMatch = dateVal.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
            if (dmyMatch) {
                date = new Date(parseInt(dmyMatch[3]), parseInt(dmyMatch[2]) - 1, parseInt(dmyMatch[1]));
            } else {
                const d = new Date(dateVal);
                if (!isNaN(d.getTime())) date = d;
            }
        }

        if (!date) return '—';
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    useEffect(() => {
        if (isOpen && guard.id) {
            loadHistory();
            loadBreakdown();
        }
    }, [isOpen, guard.id, calcMonth, calcYear]);

    const loadBreakdown = async () => {
        setLoading(true);
        try {
            const data = await getMonthlySalaryBreakdown(guard, calcYear, calcMonth, companyId);
            setBreakdown(data);
        } catch (error) {
            console.error('Error loading breakdown:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadHistory = async () => {
        setLoading(true);
        try {
            const data = await getAttendanceHistory(guard, companyId);
            setHistory(data);
        } catch (error: any) {
            console.error('Error loading history:', error);
            toast.error('Failed to load attendance history');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteHistory = async (deployment: any) => {
        if (!window.confirm('Are you sure you want to delete this historical deployment record?')) {
            return;
        }

        try {
            await deleteGuardDeploymentHistory(guard.id, deployment, companyId);
            toast.success('Historical record deleted successfully');
            onClose();
        } catch (error: any) {
            console.error('Error deleting history:', error);
            toast.error(error.message || 'Failed to delete historical record');
        }
    };

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                title={`Attendance History - ${guard.name || guard.guardId || 'Guard'}`}
                footer={
                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                        <SalarySummaryButton
                            variant="primary"
                            onClick={() => setIsGuardSalarySummaryOpen(true)}
                        >
                            <FiTrendingUp />
                            Salary Summary
                        </SalarySummaryButton>
                        <Button variant="secondary" onClick={onClose}>
                            Close
                        </Button>
                    </div>
                }
            >
                <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    <GuardInfo>
                        {guard.image ? (
                            <GuardImage src={guard.image} alt={guard.name} />
                        ) : (
                            <div style={{
                                width: 50, height: 50, borderRadius: '50%',
                                backgroundColor: theme.colors.light.background.tertiary,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: theme.typography.fontSize.xl, color: theme.colors.light.text.muted
                            }}>
                                {guard.name?.charAt(0) || '?'}
                            </div>
                        )}
                        <GuardDetails>
                            <GuardName>{guard.name || 'Unknown Guard'}</GuardName>
                            <GuardMeta>
                                {guard.guardId || 'No ID'} {guard.cnic ? `• ${guard.cnic}` : ''}
                            </GuardMeta>
                            {guard.dateOfJoining && (
                                <GuardMeta style={{ marginTop: '4px', color: theme.colors.primary[600], fontWeight: 'bold' }}>
                                    <FiClock style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                                    Starting Date: {parseDate(guard.dateOfJoining)}
                                </GuardMeta>
                            )}
                        </GuardDetails>
                    </GuardInfo>

                    <div style={{ marginBottom: theme.spacing[4] }}>
                        <TabContainer style={{ marginBottom: 0, borderBottom: 'none' }}>
                            <Tab active={activeTab === 'breakdown'} onClick={() => setActiveTab('breakdown')}>
                                Salary Breakdown
                            </Tab>
                            <Tab active={activeTab === 'attendance'} onClick={() => setActiveTab('attendance')}>
                                Attendance History
                            </Tab>
                            <Tab active={activeTab === 'deployment'} onClick={() => setActiveTab('deployment')}>
                                Deployment History
                            </Tab>
                        </TabContainer>
                    </div>

                    {loading && activeTab !== 'breakdown' ? (
                        <LoadingContainer>
                            <Spinner />
                            <p style={{ color: theme.colors.light.text.secondary }}>Loading...</p>
                        </LoadingContainer>
                    ) : activeTab === 'attendance' ? (
                        history.length > 0 ? (
                            <Table>
                                <Table.Header>
                                    <Table.Row>
                                        <Table.HeaderCell>Month & Year</Table.HeaderCell>
                                        <Table.HeaderCell>Days</Table.HeaderCell>
                                        <Table.HeaderCell>P</Table.HeaderCell>
                                        <Table.HeaderCell>A</Table.HeaderCell>
                                        <Table.HeaderCell>L</Table.HeaderCell>
                                        <Table.HeaderCell>OT</Table.HeaderCell>
                                        <Table.HeaderCell>Salary</Table.HeaderCell>
                                        <Table.HeaderCell>Adv / Balance</Table.HeaderCell>
                                        <Table.HeaderCell>Status</Table.HeaderCell>
                                        <Table.HeaderCell>Actions</Table.HeaderCell>
                                    </Table.Row>
                                </Table.Header>
                                <Table.Body>
                                    {history.map((record, index) => (
                                        <Table.Row key={index}>
                                            <Table.Cell>
                                                <strong>{getMonthName(record.month)} {record.year}</strong>
                                            </Table.Cell>
                                            <Table.Cell><Badge variant="success">{record.totalDays}</Badge></Table.Cell>
                                            <Table.Cell><Badge variant="success">{record.present}</Badge></Table.Cell>
                                            <Table.Cell><Badge variant="danger">{record.absent}</Badge></Table.Cell>
                                            <Table.Cell><Badge variant="warning">{record.leave}</Badge></Table.Cell>
                                            <Table.Cell><Badge variant="info">{record.ot || 0}</Badge></Table.Cell>
                                            <Table.Cell>
                                                <strong style={{ color: theme.colors.primary[600] }}>
                                                    Rs. {record.calculatedSalary.toLocaleString()}
                                                </strong>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                    <span style={{ fontSize: '10px', color: theme.colors.light.text.secondary }}>
                                                        Adv: Rs. {(record.advanceSalary || 0).toLocaleString()}
                                                    </span>
                                                    <strong style={{
                                                        color: (record.balance || 0) > 0 ? theme.colors.accent.danger : theme.colors.accent.success
                                                    }}>
                                                        Bal: Rs. {(record.balance || 0).toLocaleString()}
                                                    </strong>
                                                </div>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <PaymentStatusBadge status={record.paymentStatus}>
                                                    {record.paymentStatus === 'paid' ? 'Paid' : record.paymentStatus === 'unpaid' ? 'Unpaid' : 'Current'}
                                                </PaymentStatusBadge>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <button
                                                    onClick={() => setIsGuardSalarySummaryOpen(true)}
                                                    title="View Guard Salary Summary"
                                                    style={{
                                                        minWidth: 'auto', background: 'transparent', border: 'none',
                                                        cursor: 'pointer', color: theme.colors.primary[600], display: 'flex',
                                                        alignItems: 'center', justifyContent: 'center'
                                                    }}
                                                >
                                                    <FiEye size={16} />
                                                </button>
                                            </Table.Cell>
                                        </Table.Row>
                                    ))}
                                </Table.Body>
                            </Table>
                        ) : (
                            <EmptyState>
                                <EmptyStateIcon>📋</EmptyStateIcon>
                                <EmptyStateText>No attendance history available.</EmptyStateText>
                            </EmptyState>
                        )
                    ) : activeTab === 'deployment' ? (
                        <div>
                            {guard.currentDeployment ? (
                                <div style={{ marginBottom: theme.spacing[6] }}>
                                    <CardTitle>Current Status <Badge variant="success">Active</Badge></CardTitle>
                                    <HistoryCard>
                                        <DetailRow>
                                            <DetailLabel>Client:</DetailLabel>
                                            <DetailValue><strong>{guard.currentDeployment.client?.name || '—'}</strong></DetailValue>
                                        </DetailRow>
                                        <DetailRow>
                                            <DetailLabel>Type:</DetailLabel>
                                            <DetailValue>{guard.currentDeployment.client?.type || '—'}</DetailValue>
                                        </DetailRow>
                                        <DetailRow>
                                            <DetailLabel>Deployment Date:</DetailLabel>
                                            <DetailValue>{parseDate(guard.currentDeployment.postDate)}</DetailValue>
                                        </DetailRow>
                                        <DetailRow>
                                            <DetailLabel>Branch Code:</DetailLabel>
                                            <DetailValue>{guard.currentDeployment.client?.branchCode || '—'}</DetailValue>
                                        </DetailRow>
                                        <DetailRow>
                                            <DetailLabel>Monthly Salary:</DetailLabel>
                                            <DetailValue><strong>Rs. {(guard.currentDeployment.salary || guard.salary || 0).toLocaleString()}</strong></DetailValue>
                                        </DetailRow>
                                        <DetailRow>
                                            <DetailLabel>Calculation Logic:</DetailLabel>
                                            <DetailValue style={{ color: theme.colors.primary[600], fontStyle: 'italic' }}>
                                                {(() => {
                                                    const salary = guard.currentDeployment.salary || guard.salary || 0;
                                                    const parsedSalary = typeof salary === 'number' ? salary : parseFloat(String(salary).replace(/[^0-9.]/g, '')) || 0;
                                                    const postDate = guard.currentDeployment.postDate as any;
                                                    let date: Date = new Date();
                                                    if (postDate?.seconds) date = new Date(postDate.seconds * 1000);
                                                    else if (postDate) date = new Date(postDate);

                                                    const daysInMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
                                                    const dailyRate = Math.round(parsedSalary / daysInMonth);
                                                    return `Rs. ${parsedSalary.toLocaleString()} / ${daysInMonth} days ≈ Rs. ${dailyRate.toLocaleString()} per day`;
                                                })()}
                                            </DetailValue>
                                        </DetailRow>
                                        <DetailRow>
                                            <DetailLabel>Slot Number:</DetailLabel>
                                            <DetailValue>#{guard.currentDeployment.slotNumber || '—'}</DetailValue>
                                        </DetailRow>
                                    </HistoryCard>
                                </div>
                            ) : null}

                            {guard.currentOTDeployment ? (
                                <div style={{ marginBottom: theme.spacing[6] }}>
                                    <CardTitle>Current OT Status <Badge variant="info">Active</Badge></CardTitle>
                                    <HistoryCard style={{ borderLeft: `4px solid ${theme.colors.accent.info}` }}>
                                        <DetailRow>
                                            <DetailLabel>Client:</DetailLabel>
                                            <DetailValue><strong>{guard.currentOTDeployment.client?.name || '—'}</strong></DetailValue>
                                        </DetailRow>
                                        <DetailRow>
                                            <DetailLabel>Type:</DetailLabel>
                                            <DetailValue>{guard.currentOTDeployment.client?.type || '—'}</DetailValue>
                                        </DetailRow>
                                        <DetailRow>
                                            <DetailLabel>Deployment Date:</DetailLabel>
                                            <DetailValue>{parseDate(guard.currentOTDeployment.postDate)}</DetailValue>
                                        </DetailRow>
                                        <DetailRow>
                                            <DetailLabel>Branch Code:</DetailLabel>
                                            <DetailValue>{guard.currentOTDeployment.client?.branchCode || '—'}</DetailValue>
                                        </DetailRow>
                                        <DetailRow>
                                            <DetailLabel>Monthly Salary:</DetailLabel>
                                            <DetailValue><strong>Rs. {(guard.currentOTDeployment.salary || guard.salary || 0).toLocaleString()}</strong></DetailValue>
                                        </DetailRow>
                                        <DetailRow>
                                            <DetailLabel>Calculation Logic:</DetailLabel>
                                            <DetailValue style={{ color: theme.colors.primary[600], fontStyle: 'italic' }}>
                                                {(() => {
                                                    const salary = guard.currentOTDeployment.salary || guard.salary || 0;
                                                    const parsedSalary = typeof salary === 'number' ? salary : parseFloat(String(salary).replace(/[^0-9.]/g, '')) || 0;
                                                    const postDate = guard.currentOTDeployment.postDate as any;
                                                    let date: Date = new Date();
                                                    if (postDate?.seconds) date = new Date(postDate.seconds * 1000);
                                                    else if (postDate) date = new Date(postDate);

                                                    const daysInMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
                                                    const dailyRate = Math.round(parsedSalary / daysInMonth);
                                                    return `Rs. ${parsedSalary.toLocaleString()} / ${daysInMonth} days ≈ Rs. ${dailyRate.toLocaleString()} per day`;
                                                })()}
                                            </DetailValue>
                                        </DetailRow>
                                        <DetailRow>
                                            <DetailLabel>Slot Number:</DetailLabel>
                                            <DetailValue>#{guard.currentOTDeployment.slotNumber || '—'} (OT)</DetailValue>
                                        </DetailRow>
                                    </HistoryCard>
                                </div>
                            ) : null}

                            {guard.history && guard.history.length > 0 ? (
                                <div>
                                    <CardTitle>History <Badge variant="warning">{guard.history.length}</Badge></CardTitle>
                                    {guard.history.map((deployment: any, index: number) => (
                                        <HistoryCard key={index}>
                                            <DetailRow>
                                                <DetailLabel>Client:</DetailLabel>
                                                <DetailValue><strong>{deployment.client?.name || '—'}</strong></DetailValue>
                                            </DetailRow>
                                            <DetailRow>
                                                <DetailLabel>Period:</DetailLabel>
                                                <DetailValue>
                                                    {parseDate(deployment.postDate)} - {parseDate(deployment.endDate || deployment.completedAt)}
                                                </DetailValue>
                                            </DetailRow>
                                            <DetailRow>
                                                <DetailLabel>Branch Code:</DetailLabel>
                                                <DetailValue>{deployment.client?.branchCode || '—'}</DetailValue>
                                            </DetailRow>
                                            <DetailRow>
                                                <DetailLabel>Monthly Salary:</DetailLabel>
                                                <DetailValue><strong>Rs. {(deployment.salary || guard.salary || 0).toLocaleString()}</strong></DetailValue>
                                            </DetailRow>
                                            <DetailRow>
                                                <DetailLabel>Calculation Logic:</DetailLabel>
                                                <DetailValue style={{ color: theme.colors.primary[600], fontStyle: 'italic' }}>
                                                    {(() => {
                                                        const salary = deployment.salary || guard.salary || 0;
                                                        const parsedSalary = typeof salary === 'number' ? salary : parseFloat(String(salary).replace(/[^0-9.]/g, '')) || 0;
                                                        const postDate = deployment.postDate as any;
                                                        let date: Date = new Date();
                                                        if (postDate?.seconds) date = new Date(postDate.seconds * 1000);
                                                        else if (postDate) date = new Date(postDate);

                                                        const daysInMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
                                                        const dailyRate = Math.round(parsedSalary / daysInMonth);
                                                        return `Rs. ${parsedSalary.toLocaleString()} / ${daysInMonth} days ≈ Rs. ${dailyRate.toLocaleString()} per day`;
                                                    })()}
                                                </DetailValue>
                                            </DetailRow>
                                            <DetailRow>
                                                <DetailLabel>Status:</DetailLabel>
                                                <DetailValue style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <Badge variant={deployment.status === 'completed' ? 'warning' : 'success'}>
                                                        {deployment.status || 'Past'}
                                                    </Badge>
                                                    <button
                                                        onClick={() => handleDeleteHistory(deployment)}
                                                        title="Delete Historical Record"
                                                        style={{
                                                            background: 'transparent', border: 'none', cursor: 'pointer',
                                                            color: theme.colors.accent.danger, display: 'flex',
                                                            alignItems: 'center', padding: '2px', borderRadius: '4px'
                                                        }}
                                                    >
                                                        <FiTrash2 size={14} />
                                                    </button>
                                                </DetailValue>
                                            </DetailRow>
                                        </HistoryCard>
                                    ))}
                                </div>
                            ) : !guard.currentDeployment ? (
                                <EmptyState>
                                    <EmptyStateIcon>📋</EmptyStateIcon>
                                    <EmptyStateText>No deployment history available.</EmptyStateText>
                                </EmptyState>
                            ) : null}
                        </div>
                    ) : (
                        <div>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: theme.spacing[4],
                                backgroundColor: theme.colors.light.background.secondary,
                                padding: theme.spacing[3],
                                borderRadius: theme.borderRadius.md,
                                border: `1px solid ${theme.colors.light.border.light}`
                            }}>
                                <h4 style={{ fontSize: '14px', color: '#000', fontWeight: 'bold', margin: 0 }}>
                                    Salary for {getMonthName(calcMonth)} {calcYear}
                                </h4>
                                <div style={{ display: 'flex', gap: theme.spacing[2] }}>
                                    <select
                                        value={calcMonth}
                                        onChange={(e) => setCalcMonth(parseInt(e.target.value))}
                                        style={{
                                            padding: '4px 8px', borderRadius: '4px', border: `1px solid ${theme.colors.light.border.medium}`,
                                            fontSize: '12px', outline: 'none', color: '#000'
                                        }}
                                    >
                                        {Array.from({ length: 12 }).map((_, i) => (
                                            <option key={i + 1} value={i + 1}>{getMonthName(i + 1)}</option>
                                        ))}
                                    </select>
                                    <select
                                        value={calcYear}
                                        onChange={(e) => setCalcYear(parseInt(e.target.value))}
                                        style={{
                                            padding: '4px 8px', borderRadius: '4px', border: `1px solid ${theme.colors.light.border.medium}`,
                                            fontSize: '12px', outline: 'none', color: '#000'
                                        }}
                                    >
                                        {[currentDateInfo.year - 1, currentDateInfo.year, currentDateInfo.year + 1].map(y => (
                                            <option key={y} value={y}>{y}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {loading ? (
                                <LoadingContainer>
                                    <Spinner />
                                    <p style={{ color: theme.colors.light.text.secondary }}>Calculating breakdown...</p>
                                </LoadingContainer>
                            ) : breakdown.length > 0 ? (
                                <div>
                                    {breakdown.map((item, idx) => (
                                        <HistoryCard
                                            key={idx}
                                            style={{
                                                borderLeft: `4px solid ${item.isOT ? theme.colors.accent.info : theme.colors.primary[500]}`
                                            }}
                                        >
                                            <CardTitle style={{ color: '#000' }}>
                                                <span>{item.clientName} {item.isOT && <span style={{ color: theme.colors.accent.info, fontSize: '10px' }}>(OT)</span>}</span>
                                                <Badge variant={item.isOT ? "info" : "success"}>{item.presentDays} Days</Badge>
                                            </CardTitle>

                                            <DetailRow>
                                                <DetailLabel style={{ color: '#555' }}>Branch Code:</DetailLabel>
                                                <DetailValue style={{ color: '#000', fontWeight: 'bold' }}>{item.branchCode}</DetailValue>
                                            </DetailRow>

                                            <DetailRow>
                                                <DetailLabel style={{ color: '#555' }}>Calculation Period:</DetailLabel>
                                                <DetailValue style={{ color: '#000' }}>{item.startDate} to {item.endDate}</DetailValue>
                                            </DetailRow>

                                            <DetailRow>
                                                <DetailLabel style={{ color: '#555' }}>Monthly Salary:</DetailLabel>
                                                <DetailValue style={{ color: '#000', fontWeight: 'bold' }}>Rs. {item.salary.toLocaleString()}</DetailValue>
                                            </DetailRow>

                                            <DetailRow>
                                                <DetailLabel style={{ color: '#555' }}>Calculation Logic:</DetailLabel>
                                                <DetailValue style={{ color: theme.colors.primary[700], fontStyle: 'italic' }}>
                                                    Rs. {item.salary.toLocaleString()} / {item.daysInMonth} = Rs. {item.dailyRate.toLocaleString()}/day
                                                </DetailValue>
                                            </DetailRow>

                                            <DetailRow style={{ marginTop: '8px', borderTop: `1px solid ${theme.colors.light.border.light}`, paddingTop: '8px' }}>
                                                <DetailLabel style={{ color: '#000', fontWeight: 'bold' }}>Calculated Total:</DetailLabel>
                                                <DetailValue style={{ color: theme.colors.primary[600], fontSize: '14px', fontWeight: 'bold' }}>
                                                    Rs. {item.calculatedSalary.toLocaleString()}
                                                </DetailValue>
                                            </DetailRow>
                                        </HistoryCard>
                                    ))}

                                    <div style={{
                                        marginTop: theme.spacing[4], padding: theme.spacing[4],
                                        backgroundColor: `${theme.colors.primary[500]}15`,
                                        borderRadius: theme.borderRadius.lg, textAlign: 'right',
                                        border: `1px solid ${theme.colors.primary[200]}`
                                    }}>
                                        <div style={{ color: '#555', fontSize: '12px', marginBottom: '4px' }}>Total Salary for {getMonthName(calcMonth)}:</div>
                                        <strong style={{ fontSize: '20px', color: '#000' }}>
                                            Rs. {breakdown.reduce((sum, item) => sum + item.calculatedSalary, 0).toLocaleString()}
                                        </strong>
                                    </div>
                                </div>
                            ) : (
                                <EmptyState>
                                    <EmptyStateIcon>💰</EmptyStateIcon>
                                    <EmptyStateText style={{ color: '#000' }}>No active deployments found for this month.</EmptyStateText>
                                    <p style={{ fontSize: '12px', marginTop: '8px', color: '#555' }}>Please check deployment dates or select a different month.</p>
                                </EmptyState>
                            )}
                        </div>
                    )}
                </div>
            </Modal>
            <GuardSalarySummaryModal
                guard={guard}
                isOpen={isGuardSalarySummaryOpen}
                onClose={() => setIsGuardSalarySummaryOpen(false)}
                companyId={companyId}
            />
        </>
    );
};

export default AttendanceHistoryModal;
